package fr.frux.shared.data.mapper

import fr.frux.shared.domain.model.HarvestPart
import fr.frux.shared.domain.model.HarvestWindow
import fr.frux.shared.domain.model.LookalikeSeverity
import fr.frux.shared.domain.model.PlantForm
import fr.frux.shared.domain.model.RawEdibility
import fr.frux.shared.domain.model.Species
import fr.frux.shared.domain.model.SpeciesLookalike
import fr.frux.shared.domain.model.SpeciesPhoto
import fr.frux.shared.domain.model.SpeciesRef
import fr.frux.shared.domain.model.SpeciesScope
import fr.frux.shared.dto.HarvestPartDto
import fr.frux.shared.dto.LookalikeSeverityDto
import fr.frux.shared.dto.RawEdibilityDto
import fr.frux.shared.dto.SpeciesDto
import fr.frux.shared.dto.SpeciesFormDto
import fr.frux.shared.dto.SpeciesHarvestDto
import fr.frux.shared.dto.SpeciesLookalikeDto
import fr.frux.shared.dto.SpeciesPhotoDto
import fr.frux.shared.dto.SpeciesRefDto
import fr.frux.shared.dto.SpeciesScopeDto

/**
 * Traduction transport vers domaine (#109). Le `when` exhaustif fait casser la compilation ici
 * lorsqu'une forme est ajoutee au contrat, plutot que de reporter l'erreur a l'execution.
 */
fun SpeciesRefDto.toDomain(): SpeciesRef =
    SpeciesRef(
        id = id,
        commonNameFr = commonNameFr,
        scientificName = scientificName,
        form = form.toDomain(),
    )

fun SpeciesDto.toDomain(): Species =
    Species(
        id = id,
        parentId = parentId,
        rollupId = rollupId,
        scientificName = scientificName,
        commonNameFr = commonNameFr,
        form = form.toDomain(),
        selectable = selectable,
        harvests = harvests.map { it.toDomain() },
        lookalikes = lookalikes.map { it.toDomain() },
        photo = photo?.toDomain(),
        scope = scope.toDomain(),
    )

fun SpeciesScopeDto.toDomain(): SpeciesScope =
    when (this) {
        SpeciesScopeDto.TREE -> SpeciesScope.TREE
        SpeciesScopeDto.SITE_CROP -> SpeciesScope.SITE_CROP
    }

fun SpeciesPhotoDto.toDomain(): SpeciesPhoto =
    SpeciesPhoto(
        url = url,
        source = source,
        sourceUrl = sourceUrl,
        license = license,
        author = author,
    )

fun SpeciesHarvestDto.toDomain(): HarvestWindow =
    HarvestWindow(
        part = part.toDomain(),
        monthStart = monthStart,
        monthEnd = monthEnd,
        notes = notes,
        rawEdibility = rawEdibility.toDomain(),
    )

fun SpeciesLookalikeDto.toDomain(): SpeciesLookalike =
    SpeciesLookalike(
        scientificName = scientificName,
        commonNameFr = commonNameFr,
        severity = severity.toDomain(),
        distinguishingNote = distinguishingNote,
        sourceUrl = sourceUrl,
    )

fun RawEdibilityDto.toDomain(): RawEdibility =
    when (this) {
        RawEdibilityDto.EDIBLE_RAW -> RawEdibility.EDIBLE_RAW
        RawEdibilityDto.REQUIRES_PREPARATION -> RawEdibility.REQUIRES_PREPARATION
        RawEdibilityDto.TOXIC_RAW -> RawEdibility.TOXIC_RAW
    }

fun LookalikeSeverityDto.toDomain(): LookalikeSeverity =
    when (this) {
        LookalikeSeverityDto.TOXIC -> LookalikeSeverity.TOXIC
        LookalikeSeverityDto.LETHAL -> LookalikeSeverity.LETHAL
    }

fun HarvestPartDto.toDomain(): HarvestPart =
    when (this) {
        HarvestPartDto.FRUIT -> HarvestPart.FRUIT
        HarvestPartDto.FLEUR -> HarvestPart.FLEUR
        HarvestPartDto.FEUILLE -> HarvestPart.FEUILLE
        HarvestPartDto.NOIX -> HarvestPart.NOIX
        HarvestPartDto.RACINE -> HarvestPart.RACINE
    }

fun HarvestPart.toDto(): HarvestPartDto =
    when (this) {
        HarvestPart.FRUIT -> HarvestPartDto.FRUIT
        HarvestPart.FLEUR -> HarvestPartDto.FLEUR
        HarvestPart.FEUILLE -> HarvestPartDto.FEUILLE
        HarvestPart.NOIX -> HarvestPartDto.NOIX
        HarvestPart.RACINE -> HarvestPartDto.RACINE
    }

fun SpeciesFormDto.toDomain(): PlantForm =
    when (this) {
        SpeciesFormDto.ARBRE -> PlantForm.ARBRE
        SpeciesFormDto.ARBUSTE -> PlantForm.ARBUSTE
        SpeciesFormDto.HERBACEE -> PlantForm.HERBACEE
        SpeciesFormDto.LIANE -> PlantForm.LIANE
    }

fun PlantForm.toDto(): SpeciesFormDto =
    when (this) {
        PlantForm.ARBRE -> SpeciesFormDto.ARBRE
        PlantForm.ARBUSTE -> SpeciesFormDto.ARBUSTE
        PlantForm.HERBACEE -> SpeciesFormDto.HERBACEE
        PlantForm.LIANE -> SpeciesFormDto.LIANE
    }
